/**
 * Filter tariffs based on quiz answers
 * @param {Array} allTariffs - All available tariffs
 * @param {Object} answers - User's answers to quiz questions
 * @param {Object} countryData - Country data object
 * @param {string} lang - Current language (ru/en)
 * @returns {Array} Filtered tariffs
 */
export function filterTariffs(allTariffs, answers, countryData, lang) {
    if (!allTariffs || allTariffs.length === 0) {
        return [];
    }

    if (!answers || Object.keys(answers).length === 0) {
        // If no answers, return all tariffs
        return allTariffs;
    }

    return allTariffs.filter(tariff => {
        // Filter by region (question 0)
        const regionAnswer = answers[0] && answers[0][0];
        if (regionAnswer !== undefined) {
            const countryName = countryData?.name || 'country';
            const countryNameEn = countryData?.nameEn || countryData?.name || 'country';

            // Use coverage field from Sanity for more accurate filtering
            const coverage = tariff.coverage || 'local';
            const isLocal = coverage === 'local';
            const isRegional = coverage === 'regional';
            const isGlobal = coverage === 'global';

            // Check regions array
            const regions = tariff.regions || [];
            const isEuropeWide = regions.includes('Europe');
            const multiCountry = regions.length > 1;
            const includesCountry = regions.some(r =>
                r === countryName || r === countryNameEn || r.toLowerCase() === countryName.toLowerCase()
            );

            if (regionAnswer === 0) {
                // User selected "Only [country]" – show local plans only
                // Allow: local coverage OR plans that include this country (but not Europe-wide)
                if (isLocal || (includesCountry && !isEuropeWide && !isRegional && !isGlobal)) {
                    // Allow this tariff
                } else {
                    return false;
                }
            } else if (regionAnswer === 1) {
                // User selected "[country] and neighboring countries"
                // Show: regional, global, Europe-wide, or multi-country plans
                if (isRegional || isGlobal || isEuropeWide || multiCountry) {
                    // Allow this tariff
                } else {
                    return false;
                }
            }
        }

        // Filter by duration (question 1)
        const durationAnswer = answers[1] && answers[1][0];
        if (durationAnswer !== undefined && tariff.duration) {
            const validityDays = parseInt(tariff.duration);

            // Handle NaN case
            if (isNaN(validityDays)) {
                // If duration is not a number, skip this filter
            } else {
                if (durationAnswer === 0 && validityDays > 7) return false; // 1-7 days
                if (durationAnswer === 1 && (validityDays < 8 || validityDays > 15)) return false; // 8-15 days
                if (durationAnswer === 2 && (validityDays < 16 || validityDays > 30)) return false; // 16-30 days
                if (durationAnswer === 3 && validityDays <= 30) return false; // 30+ days
            }
        }

        // Filter by data amount (question 2)
        const dataAnswer = answers[2] && answers[2][0];
        if (dataAnswer !== undefined) {
            if (dataAnswer === 0) { // Up to 5 GB
                // Allow unlimited plans and plans with up to 5 GB
                if (tariff.unlimited) {
                    // Allow unlimited for "up to 5 GB" as it covers the need
                } else if (tariff.data && parseFloat(tariff.data) > 5) {
                    return false;
                }
            } else if (dataAnswer === 1) { // 5-20 GB
                // Allow unlimited plans and plans in the 5-20 GB range
                if (tariff.unlimited) {
                    // Allow unlimited as it covers the need
                } else if (tariff.data && !tariff.unlimited) {
                    const dataAmount = parseFloat(tariff.data);
                    // More flexible: allow 5-20 GB, but also allow slightly outside range
                    if (dataAmount < 3 || dataAmount > 25) {
                        return false;
                    }
                }
            } else if (dataAnswer === 2) { // 20+ GB
                // Allow unlimited and plans with 20+ GB
                if (!tariff.unlimited) {
                    // Skip Pay-As-You-Go plans (data === 0)
                    if (tariff.data && parseFloat(tariff.data) < 15) {
                        return false;
                    }
                }
            } else if (dataAnswer === 3) { // Unlimited
                if (!tariff.unlimited) {
                    return false;
                }
            }
            // dataAnswer === 4 is "Not sure" – show all plans
        }

        // Filter by language (question 3, Russian only)
        if (lang === 'ru') {
            const languageAnswer = answers[3] && answers[3][0];
            if (languageAnswer !== undefined) {
                if (languageAnswer === 0) { // Russian is necessary
                    if (!tariff.languages || !tariff.languages.includes('ru')) {
                        return false;
                    }
                }
                // languageAnswer === 1 is "English is fine" – show all plans
            }
        }

        return true;
    });
}

/**
 * Sort tariffs based on sort configuration
 * @param {Array} tariffs - Tariffs to sort
 * @param {Object} sortConfig - Sort configuration {key: string, direction: 'asc'|'desc'}
 * @returns {Array} Sorted tariffs
 */
export function sortTariffs(tariffs, sortConfig) {
    if (!sortConfig.key || !tariffs || tariffs.length === 0) {
        return tariffs;
    }

    return [...tariffs].sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
            case 'price':
                aValue = parseFloat(a.price || 0);
                bValue = parseFloat(b.price || 0);
                break;

            case 'data':
                aValue = a.unlimited ? 999999 : parseFloat(a.data || 0);
                bValue = b.unlimited ? 999999 : parseFloat(b.data || 0);
                break;

            case 'duration':
                aValue = parseInt(a.duration || 0);
                bValue = parseInt(b.duration || 0);
                break;

            case 'relevance':
                // For relevance, we can use a scoring system
                // This is a placeholder - you might want to implement more sophisticated scoring
                aValue = calculateRelevanceScore(a);
                bValue = calculateRelevanceScore(b);
                break;

            default:
                return 0;
        }

        if (aValue < bValue) {
            return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
            return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
    });
}

/**
 * Calculate relevance score for a tariff
 * @param {Object} tariff - Tariff object
 * @returns {number} Relevance score
 */
function calculateRelevanceScore(tariff) {
    let score = 0;

    // Higher score for better value
    if (tariff.pricePerGb) {
        score += (100 / parseFloat(tariff.pricePerGb));
    }

    // Bonus for unlimited plans
    if (tariff.unlimited) {
        score += 50;
    }

    // Bonus for longer validity
    if (tariff.duration) {
        score += parseInt(tariff.duration) / 10;
    }

    return score;
}

/**
 * Format days with proper plural form
 * @param {number} count - Number of days
 * @param {string} language - Language code (ru/en)
 * @returns {string} Formatted string
 */
export function formatDays(count, language) {
    if (language === 'en') {
        return count === 1 ? `${count} day` : `${count} days`;
    } else {
        // Russian pluralization
        if (count === 1) {
            return `${count} день`;
        } else if (count >= 2 && count <= 4) {
            return `${count} дня`;
        } else {
            return `${count} дней`;
        }
    }
}

